import { z } from 'zod';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Anchor } from '@mantine/core';

import routes from 'utils/routes';
import Form from 'components/form';
import SEO from 'components/SEO';
import AuthContext from 'context/AuthContext';
import useFormHandler from 'hooks/useFormHandler';
import { useDebouncedValue } from '@mantine/hooks';
import TrackingContext from 'context/TrackingContext';
import RightSideFeatures from 'components/RightSideFeatures';
import { createEmailSchema } from 'utils/schemaBuilder';

const formSchema = z
  .object({
    password: z.string(),
    terms: z.boolean(),
    valid_password: z.boolean(),
    confirm_password: z.string(),
    email: createEmailSchema(true),
  })
  .refine((data: { password: string; confirm_password: string }) => data.password === data.confirm_password, {
    message: "The passwords don't match",
    path: ['confirm_password'],
  })
  .refine((data: { terms: boolean }) => data.terms === true, {
    message: 'You must accept the terms and conditions',
    path: ['terms'],
  });

type FormSchema = z.infer<typeof formSchema>;

export default function Register() {
  const navigate = useNavigate();
  const [passwordValid, setPasswordValid] = useState(false);
  const [emailCheckLoading, setEmailCheckLoading] = useState(false);
  const [checkedPassword, setCheckedPassword] = useState('');
  const [emailValue, setEmailValue] = useState('');
  const [debouncedEmail] = useDebouncedValue(emailValue, 500);
  const {
    actions: { setRegisterEmail, setRegisterPassword, checkAvailableEmail },
  } = useContext(AuthContext);
  const {
    actions: { trackSignUpStart },
  } = useContext(TrackingContext);

  const { form, loading, error, setError } = useFormHandler(formSchema, {
    initialValues: {
      email: '',
      password: '',
      terms: false,
      confirm_password: '',
      valid_password: false,
    },
  });

  useEffect(() => {
    const validateEmail = async () => {
      if (!debouncedEmail) {
        setEmailCheckLoading(false);
        return;
      }

      const errorMessage = 'Email is already in use';

      try {
        const isAvailable = await checkAvailableEmail(debouncedEmail);

        if (!isAvailable) {
          form.setErrors({
            email: errorMessage,
          });
        }
      } catch (error: any) {
        if (error.status === 400) {
          form.setErrors({
            email: errorMessage,
          });
        }
      } finally {
        setEmailCheckLoading(false);
      }
    };

    validateEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedEmail]);

  const onSubmit = async (values: FormSchema) => {
    try {
      setRegisterEmail(values.email as string);
      setRegisterPassword(values.password as string);
      trackSignUpStart();
      navigate(routes.onboardingPath.href);
    } catch (error) {
      setError('Error. Please try again.');
    }
  };

  const getConfirmPasswordError = () => {
    if (!form.values.confirm_password) {
      return undefined;
    }

    return form.values.confirm_password !== form.values.password
      ? 'Passwords do not match'
      : undefined;
  };

  return (
    <>
      <SEO 
        title="Get Your Free AI Receptionist - Sign Up Today"
        description="Get your free Nucleus AI receptionist. Never miss another customer call with our 24/7 AI phone system that learns your business instantly."
        keywords="free AI receptionist, virtual assistant, AI phone system, business automation, customer service AI"
      />
      <div className="flex-1 grid grid-cols-1 md:grid-cols-2 min-h-screen overflow-hidden">
        <div className="min-h-screen overflow-y-auto">
          <nav className="h-[70px] sticky top-0 w-full px-4 md:px-6 flex items-center justify-between">
            <a
              href="https://nucleus.com/"
              className="flex items-center gap-4"
              tabIndex={9}
            >
              <img src="/images/nucleus-logo.svg" alt="Nucleus Logo" className="h-8" />
            </a>
          </nav>
          <div className="md:flex-col items-center justify-center  lg:items-center signup-container extend px-4 md:px-0">
            <div className="mx-auto">
              <div className="text-center mb-3 max-w-[600px] mx-auto">
                <h1 className="text-xl md:text-2xl font-semibold text-[#5E6AB2] mb-1">
                  Get Your Free AI Receptionist
                </h1>
                <h2 className="text-base md:text-lg font-semibold text-gray-700 mb-4 text-center">
                  Never Miss Another Customer Call
                </h2>
              </div>
              <div className="border custom-border-color bg-white rounded-xl p-4 md:p-6 md:w-5/6 xl:w-[440px] mx-auto">
                <Form form={form} error={error} onSubmit={onSubmit}>
                  <Form.Input
                    name="email"
                    type="email"
                    label="Email"
                    placeholder="name@example.com"
                    required
                    tabIndex={1}
                    onChange={(value: string | number) => {
                      setEmailCheckLoading(true);
                      setEmailValue(value as string);
                      form.setFieldError('email', null);
                    }}
                    onBlur={() => {
                      form.validateField('email');
                    }}
                  />
                  <Form.Password
                    name="password"
                    label="Password"
                    placeholder="Password"
                    tabIndex={2}
                    onUpdate={(value: string) => setCheckedPassword(value)}
                  />
                  {checkedPassword && (
                    <Form.PasswordCheck
                      value={checkedPassword}
                      isValid={passwordValid}
                      validatePassword={(isValid: boolean) => {
                        setPasswordValid(isValid);
                        form.setValues({ valid_password: isValid });
                      }}
                    />
                  )}
                  <Form.Password
                    name="confirm_password"
                    label="Confirm Password"
                    placeholder="Confirm password"
                    tabIndex={3}
                    error={getConfirmPasswordError()}
                  />
                  <div className="flex items-center gap-2 mt-4">
                    <Form.Checkbox
                      name="terms"
                      tabIndex={4}
                      styles={{
                        label: {
                          fontSize: '15px',
                          fontWeight: 500,
                          color: 'var(--text-color)',
                          opacity: 0.85,
                          cursor: 'pointer',
                          lineHeight: 1.4,

                          a: {
                            color: 'var(--text-color)',
                            borderBottom: '1px solid var(--text-color)',
                            textDecoration: 'none',
                            transition: 'opacity 0.2s ease',
                            fontWeight: 600,

                            '&:hover': {
                              textDecoration: 'none',
                              opacity: 0.8,
                            },
                          },
                        },
                        input: {
                          cursor: 'pointer',
                          width: '20px',
                          height: '20px',
                          borderRadius: '4px',
                          border: '1.5px solid #9CA3AF',
                          transition: 'all 0.2s ease',

                          '&:hover': {
                            borderColor: '#4B559E',
                          },

                          '&:checked': {
                            backgroundColor: '#4B559E',
                            borderColor: '#4B559E',
                          },
                        },
                      }}
                      label={
                        <>
                          I agree to the{' '}
                          <Anchor
                            href={routes.termsPath.href}
                            target="_blank"
                            inherit
                            tabIndex={7}
                            className="text-xs"
                          >
                            Terms of Use
                          </Anchor>
                          {' and '}
                          <Anchor
                            href={routes.privacyPath.href}
                            target="_blank"
                            inherit
                            tabIndex={8}
                            className="text-xs"
                          >
                            Privacy Policy
                          </Anchor>
                        </>
                      }
                    />
                  </div>
                  <Form.Submit
                    loading={loading}
                    mt={16}
                    tabIndex={5}
                    disabled={!passwordValid || emailCheckLoading}
                  >
                    Sign Up
                  </Form.Submit>
                </Form>
                <div className="flex items-center text-xs custom-text-color font-medium gap-2 mt-3 justify-center text-center">
                  Already have an account?{' '}
                  <a href={routes.signInPath.href} tabIndex={6} className="font-semibold text-[--primary-color]">
                    Sign in
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        <RightSideFeatures />
      </div>
    </>
  );
}
